/**
 * @package   EGI / Frontend — wishlist
 * @author    Padmin D. Curtis (AI Partner OS3.0) for Fabio Cherici
 * @version   1.0.0 (M-EGI-373)
 * @date      2026-07-03
 * @purpose   Modulo pagina /wishlist (design §1.3): guest → clona il <template>
 *            gemello per ogni voce localStorage valorizzando SOLO textContent/
 *            href/src (MAI markup-injection da stringhe — CWE-79) dopo aver
 *            tolto lo skeleton; auth → item SSR, qui solo rimozione/contatore.
 *            Focus management alla rimozione (§7.4): remove successivo →
 *            precedente → H1.
 */

import type { WishlistStore } from './store';
import { CHANGED_EVENT, type WishlistChangedDetail, type WishlistItem, type WishlistLabels } from './types';

/** URL immagine ammesso: path same-origin o URL assoluto della stessa origin. */
function isSafeImageUrl(url: string): boolean {
    if (url.startsWith('/')) {
        return true;
    }
    return url.startsWith(window.location.origin + '/');
}

function resolveCountText(count: number, labels: WishlistLabels): string {
    if (count === 1) {
        return labels.countOne;
    }
    return labels.countOther.replace(':count', String(count));
}

export function initWishlistPage(
    store: WishlistStore,
    labels: WishlistLabels,
    announce: (message: string) => void,
): void {
    const root = document.querySelector<HTMLElement>('[data-wishlist-page]');
    if (!root) {
        return; // non siamo sulla pagina wishlist
    }

    const list = root.querySelector<HTMLElement>('[data-wishlist-list]');
    const empty = root.querySelector<HTMLElement>('[data-wishlist-empty]');
    const countEl = root.querySelector<HTMLElement>('[data-wishlist-page-count]');
    const heading = root.querySelector<HTMLElement>('[data-wishlist-heading]');
    const skeleton = root.querySelector<HTMLElement>('[data-wishlist-skeleton]');
    const template = document.querySelector<HTMLTemplateElement>('#wishlist-item-template');
    const showUrlTemplate = root.dataset.showUrlTemplate ?? '';
    const isGuest = root.dataset.authenticated !== '1';

    if (!list || !empty || !countEl) {
        return; // markup incompleto → modulo spento (shell SSR resta leggibile)
    }

    function syncEmptyState(count: number): void {
        // Gotcha organo: visibilità via display, MAI attributo hidden con utility flex.
        (empty as HTMLElement).style.display = count === 0 ? '' : 'none';
        (countEl as HTMLElement).style.display = count === 0 ? 'none' : '';
        if (count > 0) {
            (countEl as HTMLElement).textContent = resolveCountText(count, labels);
        }
    }

    /** Clona il template gemello: valorizza SOLO textContent, href e src tipizzati. */
    function renderGuestItem(item: WishlistItem): HTMLElement | null {
        if (!template) {
            return null;
        }
        const fragment = template.content.cloneNode(true) as DocumentFragment;
        const node = fragment.querySelector<HTMLElement>('[data-wishlist-item]');
        if (!node) {
            return null;
        }
        node.dataset.egiId = item.id;

        const title = node.querySelector<HTMLElement>('[data-item-title]');
        if (title) {
            title.textContent = item.title;
        }

        const showUrl = showUrlTemplate.replace('__ID__', encodeURIComponent(item.id));
        node.querySelectorAll<HTMLAnchorElement>('[data-item-link]').forEach((link) => {
            link.href = showUrl;
        });

        const image = node.querySelector<HTMLImageElement>('[data-item-image]');
        if (image && isSafeImageUrl(item.imageUrl)) {
            image.src = item.imageUrl;
        }

        const removeBtn = node.querySelector<HTMLElement>('[data-wishlist-remove]');
        if (removeBtn) {
            removeBtn.dataset.egiId = item.id;
            removeBtn.setAttribute('aria-label', labels.removeNamed.replace(':title', item.title));
        }

        return node;
    }

    function renderGuestList(): void {
        skeleton?.remove(); // hydration guard: lo skeleton esce solo a lettura completata
        const items = [...store.list()].sort((a, b) => b.addedAt - a.addedAt); // più recenti prima (parity server)
        for (const item of items) {
            const node = renderGuestItem(item);
            if (node) {
                (list as HTMLElement).appendChild(node);
            }
        }
        syncEmptyState(items.length);
    }

    /** §7.4 — focus mai su nodo staccato: remove successivo → precedente → H1. */
    function focusAfterRemoval(removed: HTMLElement): void {
        const rows = Array.from((list as HTMLElement).querySelectorAll<HTMLElement>('[data-wishlist-item]'));
        const index = rows.indexOf(removed);
        const next = rows[index + 1] ?? rows[index - 1];
        const target = next?.querySelector<HTMLElement>('[data-wishlist-remove]') ?? heading;
        target?.focus();
    }

    async function onRemoveClick(button: HTMLElement): Promise<void> {
        const id = button.dataset.egiId ?? '';
        const row = button.closest<HTMLElement>('[data-wishlist-item]');
        if (id === '' || !row || button.getAttribute('aria-busy') === 'true') {
            return;
        }

        button.setAttribute('aria-busy', 'true'); // mai disabled: il focus resta (§7.5)
        button.style.opacity = '0.5';
        try {
            focusAfterRemoval(row); // sposta il focus PRIMA di staccare il nodo
            row.remove();
            syncEmptyState(store.count() - 1); // ottimistico; riallineato dall'evento changed
            await store.remove(id);
        } catch {
            // Rollback: lo store ha già ripristinato lo stato; qui si ripristina il nodo.
            (list as HTMLElement).appendChild(row);
            syncEmptyState(store.count());
            announce(labels.error);
        } finally {
            button.removeAttribute('aria-busy');
            button.style.opacity = '';
        }
    }

    // Click delegato sul root pagina per i bottoni rimuovi (anche quelli clonati dal template).
    root.addEventListener('click', (event) => {
        const target = event.target;
        if (!(target instanceof Element)) {
            return;
        }
        const button = target.closest<HTMLElement>('[data-wishlist-remove]');
        if (button) {
            event.preventDefault();
            void onRemoveClick(button);
        }
    });

    // Il contatore segue SEMPRE lo store (unica fonte) — niente doppia live region (§1.3).
    document.addEventListener(CHANGED_EVENT, (event) => {
        const detail = (event as CustomEvent<WishlistChangedDetail>).detail;
        syncEmptyState(detail.count);
    });

    void store.ready.then(() => {
        if (isGuest) {
            renderGuestList();
        } else {
            syncEmptyState(store.count());
        }
    });
}
